import { useMemo, useState } from "react";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import PageLayout from "components/PageLayout";
import OutlinedCard from "components/OutlinedCard";
import PageTitle from "components/PageTitle";
import AbsenceTable from "./components/AbsenceTable";
import AbsenceFilters from "./components/AbsenceFilters";
import SearchField from "components/SearchField";
import { useSortedData } from "lib/hooks/useSortedData";
import AbsenceSchedule from "./components/AbsenceSchedule";
import FilterMenu from "components/FilterMenu";
import ViewSelectButton from "./components/ViewSelectButton";
import { ScheduleScale } from "features/schedule-view/ScheduleContext";
import { useAbsenceSearch } from "./useAbsenceSearch";
import { useGetAllUsersLimitedQuery } from "app/api/users";
import { UserRole } from "features/auth/types/user";

const AbsencePage: React.FC = () => {
  const [view, setView] = useQueryState<"LIST" | ScheduleScale>(
    "view",
    parseAsStringLiteral(["LIST", "WEEKS", "MONTHS", "WORKWEEKS"]).withDefault("WORKWEEKS"),
  );

  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showDayNumbers, setShowDayNumbers] = useState(true);

  const {
    search,
    filters,
    activeFiltersCount,
    data,
    isLoading,
    setSearch,
    setFilters,
    resetFilters,
  } = useAbsenceSearch({ view });

  const { data: employees } = useGetAllUsersLimitedQuery({
    roles: [
      UserRole.ADMIN,
      UserRole.USER_ADMIN,
      UserRole.PAYROLL,
      UserRole.INTERNAL_INSTALLER,
      UserRole.EMPLOYEE,
      UserRole.SALES,
    ],
    departmentIds: filters.departmentIds,
    officeIds: filters.officeIds,
  });

  const {
    data: sortedData,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
  } = useSortedData(data || [], [
    { key: "userName", defaultSortDir: "asc" },
    { key: "from" },
    { key: "to" },
  ]);

  const sortedEmployees = useMemo(
    () =>
      employees
        ?.filter((emp) => showAllUsers || data?.some((entry) => entry.userId === emp.id))
        .sort((a, b) =>
          sortBy === "userName" && sortDir === "desc"
            ? b.userName.localeCompare(a.userName)
            : a.userName.localeCompare(b.userName),
        ) || [],
    [employees, sortBy, sortDir, data, showAllUsers],
  );

  return (
    <PageLayout maxWidth={false}>
      <PageTitle>Frånvaro</PageTitle>

      <OutlinedCard>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <SearchField label="Sök namn" value={search} onChange={(e) => setSearch(e.target.value)} />

          <FilterMenu
            tooltip="Välj filter"
            activeFiltersCount={activeFiltersCount}
            onReset={resetFilters}
          >
            <Stack spacing={2}>
              <AbsenceFilters filters={filters} onChange={setFilters} />
            </Stack>
          </FilterMenu>

          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}>
            <FormControlLabel
              label="Alla medarbetare"
              labelPlacement="start"
              hidden={view === "LIST"}
              control={
                <Checkbox
                  checked={showAllUsers}
                  onChange={(e) => setShowAllUsers(e.target.checked)}
                />
              }
            />

            <FormControlLabel
              label="Visa dagnummer"
              labelPlacement="start"
              hidden={view === "LIST"}
              sx={{ mr: 1 }}
              control={
                <Checkbox
                  checked={showDayNumbers}
                  onChange={(e) => setShowDayNumbers(e.target.checked)}
                />
              }
            />
          </Box>

          <ViewSelectButton view={view} setView={setView} />
        </Box>

        {isLoading ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
            <CircularProgress />
            <Typography>Hämtar frånvaro...</Typography>
          </Box>
        ) : view === "LIST" ? (
          <AbsenceTable
            entries={sortedData}
            sortBy={sortBy}
            sortDir={sortDir}
            setSortBy={setSortBy}
            setSortDir={setSortDir}
          />
        ) : (
          <AbsenceSchedule
            rows={sortedEmployees}
            entries={sortedData}
            view={view}
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            showDayNumbers={showDayNumbers}
            sortBy={sortBy}
            sortDir={sortDir}
            setSortBy={setSortBy}
            setSortDir={setSortDir}
          />
        )}
      </OutlinedCard>
    </PageLayout>
  );
};

export default AbsencePage;
